import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Box, Paper, SimpleGrid, Skeleton, Stack, Text as MantineText } from '@mantine/core';
import { IconCheck } from '@tabler/icons';
import { ToastS } from '../../Service/ToastS';
import {
  fetchLatestRequestOfWebForm,
  patchCustomAttributeRequestField,
  patchFormAttributeRequestField,
} from '../../Service/restapi/webformService';
import AssignField from './AssignField';

const skeletonKeys = ['first', 'second', 'third'];

const assignedBadgeStyles = (theme) => ({
  root: {
    backgroundColor: theme.colorScheme === 'dark' ? 'rgba(51, 154, 240, 0.22)' : '#E7F0FF',
    color: theme.colorScheme === 'dark' ? '#D0EBFF' : '#17458F',
    textTransform: 'none',
    fontWeight: 700,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 4,
    color: 'currentColor',
  },
});

const SelectFieldList = ({ webForm }) => {
  const [mapping, setMapping] = useState(webForm.formAttributesMap ?? []);
  const [customAttributes, setCustomAttributes] = useState(webForm.customAttributeMappings ?? []);
  const [lastRequest, setLastRequest] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const webFormField = 'webform.field.';

  async function addMapping(websiteField, option) {
    if (!option) {
      return;
    }

    try {
      const resp = option.attribute
        ? await patchCustomAttributeRequestField(webForm.id, websiteField.keyValue, option.value)
        : await patchFormAttributeRequestField(webForm.id, websiteField.keyValue, option.value);

      if (!resp.ok) {
        ToastS.generalError();
        return;
      }

      const newWebForm = await resp.json();
      setCustomAttributes(newWebForm.customAttributeMappings ?? []);
      setMapping(newWebForm.formAttributesMap ?? []);
    } catch (_error) {
      ToastS.generalError();
    }
  }

  useEffect(() => {
    setLoading(true);
    setLastRequest(undefined);
    setMapping(webForm.formAttributesMap ?? []);
    setCustomAttributes(webForm.customAttributeMappings ?? []);

    fetchLatestRequestOfWebForm(webForm.id)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((request) => {
            if (request !== null && request !== undefined && request.originalFormData) {
              setLastRequest(request.originalFormData);
            }
          });
        }
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [webForm]);

  const visibleFields = (lastRequest ?? []).filter(
    (field) => field.keyValue !== 'pathId' && field.keyValue !== 'hostname',
  );
  const mappedFields = visibleFields.filter(
    (field) =>
      mapping.find((m) => m.keyValue === field.keyValue && m.value && m.value !== 'undefined') ||
      customAttributes.find((m) => m.keyValue === field.keyValue && m.value),
  );

  const selectList = visibleFields.map((field) => {
    const fieldMapping = mapping.find((m) => m.keyValue === field.keyValue);
    const customAttributeFieldMapping = customAttributes.find((m) => m.keyValue === field.keyValue);

    return (
      <AssignField
        key={field.keyValue}
        targetFieldId={fieldMapping ? fieldMapping.value : customAttributeFieldMapping?.value}
        i18nPreLabel={webFormField}
        field={field.keyValue}
        exampleValue={field}
        onChange={(val) => {
          addMapping(field, val);
        }}
      />
    );
  });

  return (
    <Stack spacing="md">
      <Box>
        <MantineText weight={700}>Daten-Felder zuweisen</MantineText>
        <MantineText size="sm" color="dimmed">
          Links steht der zuletzt empfangene Wert aus Deiner Webseite. Rechts wählst Du das passende bookitup Feld,
          damit neue Anfragen automatisch ausgefüllt werden. Nicht jedes Feld muss zugeordnet werden.
        </MantineText>
      </Box>

      {loading && (
        <Stack spacing="xs">
          {skeletonKeys.map((key) => (
            <Skeleton key={key} height={64} radius="md" />
          ))}
        </Stack>
      )}

      {!loading && visibleFields.length === 0 && (
        <Paper withBorder radius="md" p="md">
          <MantineText size="sm" color="dimmed">
            Noch keine Formularfelder empfangen. Sende das Formular einmal ab, damit bookitup die Felder anzeigen kann.
          </MantineText>
        </Paper>
      )}

      {!loading && visibleFields.length > 0 && (
        <Stack spacing="xs">
          <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]} px="sm">
            <MantineText size="xs" weight={700} color="dimmed">
              Daten von Deiner Webseite
            </MantineText>
            <MantineText size="xs" weight={700} color="dimmed">
              bookitup Feld
            </MantineText>
          </SimpleGrid>
          {selectList}
        </Stack>
      )}
    </Stack>
  );
};

SelectFieldList.propTypes = {
  webForm: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    formAttributesMap: PropTypes.arrayOf(
      PropTypes.shape({
        keyValue: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
    customAttributeMappings: PropTypes.arrayOf(
      PropTypes.shape({
        keyValue: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  }).isRequired,
};

export default SelectFieldList;
